import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { Dialog, PillView, Toolbar, useAction } from "../../../components/ui";
import { EmptyState, ErrorState, Loading } from "../../../components/States";
import { date, time, today as todayIn } from "../../../lib/format";
import { useLang } from "../../creator/sprache";
import { dateOf } from "../../creator/lang";
import { mailLanguageName } from "../../creator/mailsprache";
import { zonenAuswahl, zonenName } from "../zonen";
import {
  DEFAULT_REMINDERS, DIGEST_HOURS, answerPaymentCheck, assignTask, closeTask, dismissNotification, dismissNotifications, loadReminders, markNotificationsRead,
  restoreNotifications, saveAbsence, saveDesktopPreference, saveEmailChat, saveEmailInfoOff, saveEmailTasks, saveMemberMailPrefs, saveReminders,
  saveTalentPreferences, saveTimeZone, type Channels, type NotificationScope, type ReminderSettings,
} from "./data";
import { useChannels, useDoneNotifications, useMembers, useOpenNotifications, useStaffNotificationContext, useUnreadData } from "./hooks";
import {
  CATEGORIES, categoryLabel, countLabel, creatorMailHints, groupByDay, matchesState, personName, pill, shortWhen, sortTasks, viewNotification, whenWithDay,
  type Category, type NotificationContext, type NotificationView, type Portal, type StateFilter,
} from "./model";
import { desktopPermission } from "./unread";
import "./nachrichten.css";
import { istCreatorKonto, t, ta } from "../../../lib/sprache";
import { Mehr } from "../../../components/ZeilenMenue";
import { MehrLaden, useSeitenweise } from "../../../components/MehrLaden";
import { useHandy } from "../../../lib/handy";
import { Klappabschnitt } from "../../agentur/einstellungen/Klappabschnitt";

/**
 * Benachrichtigungen (11_app_agentur/benachrichtigungen.html; im Creator-Portal dieselben Bausteine in Du-Form).
 * Umfang wie die Glocke des Altportals (AD-201 … AD-207, TA-160 … TA-168): offene Aufgaben zuerst, Hinweise nach Tagen,
 * neue Nachrichten, Filter nach Stand und Art, Suche, gelesen markieren, Hinweise ausblenden, Zahlungsnachfrage direkt
 * beantworten, Erledigtes nachschlagen. Aufgaben verschwinden nur, wenn sie erledigt sind (A-25).
 * 0056: Creator-Portal auf Englisch (Befund 9), Aufgaben übernehmen (Befund 16), Kanäle mit Tagesübersicht, eigener
 * Zeitzone, persönlicher Antwortadresse und Abwesenheit mit Vertretung.
 */

export interface NotificationsPageProps {
  portal: Portal;
  scope: NotificationScope;
  timeZone: string;
  eyebrow: string;
  /** Gegenüber im Chat: „Creator“ (Agentur) bzw. Name der Agentur (Creator) */
  counterpart: string;
  calendarHref: string;
  channels: ReactNode;
}

export function NotificationsPage(props: NotificationsPageProps) {
  const { portal, scope, timeZone } = props;
  const lang = useLang();
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const me = api.session()?.userId ?? null;
  const today = todayIn(timeZone);
  const [stateFilter, setStateFilter] = useState<StateFilter>("offen");
  const [category, setCategory] = useState<Category | "alle">("alle");
  const [search, setSearch] = useState("");
  const open = useOpenNotifications(scope);
  const done = useDoneNotifications(scope, me, stateFilter === "erledigt");
  const unread = useUnreadData({ portal, orgId: scope.orgId, talentId: scope.talentId ?? null });
  const all = useMemo(() => [...(open.data ?? []), ...(stateFilter === "erledigt" ? done.data ?? [] : [])], [open.data, done.data, stateFilter]);
  const invoiceIds = useMemo(
    () => (portal === "agentur" ? [...new Set(all.filter((n) => n.link_kind === "invoice" && n.link_id).map((n) => n.link_id!))] : []),
    [all, portal],
  );
  const context = useStaffNotificationContext(scope.orgId, invoiceIds, portal === "agentur" && open.isSuccess);
  const { busy, run } = useAction();
  // Sammel-Ausblenden (Befund 16): nur die angezeigte Auswahl, mit Rückfrage und „Wieder einblenden“
  const [frageAusblenden, setFrageAusblenden] = useState(false);
  const [zuletzt, setZuletzt] = useState<string[]>([]);
  const [schliessen, setSchliessen] = useState<NotificationView | null>(null);

  const views = useMemo(() => {
    const talents = new Map((context.data?.talents ?? []).map((x) => [x.id, x]));
    const ctx: NotificationContext = {
      portal, today, timeZone, lang,
      talentName: (id) => (id && talents.has(id) ? personName(talents.get(id)) : null),
      invoice: (id) => (id ? context.data?.invoices[id] : undefined),
    };
    return all.map((n) => viewNotification(n, ctx));
  }, [all, context.data, portal, today, timeZone, lang]);

  // R-03 (Umbauten 03.11.2026): Hinweise und Erledigtes seitenweise (20 je Portion), Aufgaben immer ganz;
  // Filter, Art und Suche gelten für alles, ein Wechsel beginnt wieder mit der ersten Portion
  const q = search.trim().toLowerCase();
  const shown = useMemo(() => views.filter((v) => matchesState(v.item, stateFilter) && (category === "alle" || v.category === category) && (!q || v.search.includes(q))), [views, stateFilter, category, q]);
  const infosOffen = useMemo(() => shown.filter((v) => v.item.state === "open" && !v.item.is_task).sort((a, b) => b.item.created_at.localeCompare(a.item.created_at)), [shown]);
  const erledigte = useMemo(() => shown.filter((v) => v.item.state !== "open").sort((a, b) => (b.item.closed_at ?? b.item.created_at).localeCompare(a.item.closed_at ?? a.item.created_at)), [shown]);
  const handy = useHandy();
  // Prüf-Chat Nachtest (03.11.2026), R-03: auch am Rechner 20 je Portion (bei 26 Aufgaben und 39 Hinweisen griff die Portion von
  // 50 nie, die Seite war 7.729 px lang); Aufgaben bleiben vollständig
  const portion = 20;
  const infoSeite = useSeitenweise(infosOffen, portion, `${stateFilter}|${category}|${q}`);
  const erledigtSeite = useSeitenweise(erledigte, portion, `${stateFilter}|${category}|${q}`);

  if (open.error) return <ErrorState error={open.error} retry={() => void open.refetch()} />;
  if (open.isPending || (portal === "agentur" && context.isPending && !context.error)) return <Loading />;

  const STATE_FILTERS: { key: StateFilter; label: string }[] = [
    { key: "offen", label: t("Offen", "Open") },
    { key: "ungelesen", label: t("Ungelesen", "Unread") },
    { key: "erledigt", label: t("Erledigt", "Done") },
  ];
  const openViews = views.filter((v) => v.item.state === "open");
  const unreadIds = openViews.filter((v) => v.item.unread).map((v) => v.item.id);
  // Ausblenden wirkt nur auf die Hinweise, die gerade angezeigt werden (Filter und Suche gelten)
  const infoIds = shown.filter((v) => v.dismissible).map((v) => v.item.id);
  const taskCount = openViews.filter((v) => v.item.is_task).length;
  const tasks = sortTasks(shown.filter((v) => v.item.state === "open" && v.item.is_task));
  const infoGroups = groupByDay(infoSeite.sichtbar, (v) => v.item.created_at, today, timeZone, lang);
  const closedGroups = groupByDay(erledigtSeite.sichtbar, (v) => v.item.closed_at ?? v.item.created_at, today, timeZone, lang);
  const chat = unread.data;
  const showChat = !!chat && chat.count.messages > 0 && stateFilter !== "erledigt" && category === "alle" && !q;
  const chatHref = portal === "agentur"
    ? (chat && chat.latest.length === 1 ? `/agentur/nachrichten/${chat.latest[0]!.talentId}` : "/agentur/nachrichten")
    : "/creator/nachrichten";
  const filtered = stateFilter !== "offen" || category !== "alle" || !!q;
  const nothing = tasks.length === 0 && infoGroups.length === 0 && closedGroups.length === 0 && !showChat;
  const categories = CATEGORIES[portal];
  const tm = (iso: string) => time(iso, timeZone, lang === "en" ? "en-GB" : "de-DE");

  // Prüf-Chat CP-019 (02.11.2026): „Keine offenen Aufgaben“ neben „Offen · 1“ (eine Korrektur) las sich widersprüchlich. Der Kopf
  // zählt jetzt wie der Reiter „Offen“: Aufgaben und offene Hinweise getrennt, zusammen genau die Zahl am Reiter.
  const infoCount = openViews.length - taskCount;
  const summary = [
    taskCount ? (lang === "en" ? countLabel(taskCount, "open task", "open tasks") : countLabel(taskCount, "offene Aufgabe", "offene Aufgaben")) : null,
    infoCount ? (lang === "en" ? countLabel(infoCount, "open notice", "open notices") : countLabel(infoCount, "offener Hinweis", "offene Hinweise")) : null,
    !taskCount && !infoCount ? t("Keine offenen Aufgaben", "No open tasks") : null,
    unreadIds.length ? t(`${unreadIds.length} neu`, `${unreadIds.length} new`) : null,
    chat?.count.messages ? (lang === "en" ? countLabel(chat.count.messages, "unread message", "unread messages") : countLabel(chat.count.messages, "ungelesene Nachricht", "ungelesene Nachrichten")) : null,
  ].filter(Boolean).join(" · ");

  const markOne = (v: NotificationView) => {
    if (v.item.state === "open" && v.item.unread) void markNotificationsRead(api, [v.item.id]).catch(() => undefined);
  };
  const ausblenden = async () => {
    const ids = [...infoIds];
    const n = await run("alle-ausblenden", () => dismissNotifications(api, scope, ids));
    setFrageAusblenden(false);
    if (n !== undefined) setZuletzt(ids);
  };
  const row = (v: NotificationView, when: string) => {
    const task = portal === "agentur" && v.item.state === "open" && v.item.is_task;
    return (
      <NotificationRow key={`${v.item.state}:${v.item.id}`} v={v} when={when} busy={busy !== null} onOpen={markOne} lang={lang}
        onClose={task ? () => setSchliessen(v) : undefined}
        onAssign={task && v.item.assigned_to !== me ? () => void run("uebernehmen", () => assignTask(api, v.item.id), t("Aufgabe übernommen. Das Team sieht es in der Glocke.", "Task taken over. The team sees it in the notifications.")) : undefined}
        onRelease={task && v.item.assigned_to === me ? () => void run("abgeben", () => assignTask(api, v.item.id, { release: true }), t("Aufgabe abgegeben.", "Task handed back.")) : undefined}
        onRead={() => void run("gelesen", () => markNotificationsRead(api, [v.item.id]))}
        onDismiss={() => void run("ausblenden", () => dismissNotification(api, v.item.id), t("Hinweis ausgeblendet. Du findest ihn unter „Erledigt“.", "Notice hidden. You can find it under “Done”."))}
        onAnswer={(response) => void run("antwort", () => answerPaymentCheck(api, v.answerInvoice!, response),
          response === "paid" ? t("Danke, deine Zahlungsmeldung ist bei deiner Agentur.", "Thanks, your agency has your payment report.")
            : t("Danke, deine Agentur sieht: Das Geld fehlt noch.", "Thanks, your agency can see the money is still missing."))} />
    );
  };

  return (
    <main id="main" className="content tk-glocke">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{props.eyebrow}</div>
          <h1>{t("Benachrichtigungen", "Notifications")}</h1>
          <p>{summary}</p>
        </div>
        <div className="row">
          <button type="button" disabled={!unreadIds.length || busy !== null}
            onClick={() => void run("alle-gelesen", () => markNotificationsRead(api, unreadIds), t("Alle Benachrichtigungen sind als gelesen markiert.", "All notifications are marked as read."))}>
            {t("Alle als gelesen markieren", "Mark all as read")}
          </button>
          <button type="button" disabled={!infoIds.length || busy !== null || stateFilter === "erledigt"} aria-describedby="hinweise-ausblenden"
            onClick={() => setFrageAusblenden(true)}>
            {filtered ? t("Angezeigte Hinweise ausblenden", "Hide shown notices") : t("Hinweise ausblenden", "Hide notices")}
          </button>
          <small id="hinweise-ausblenden" className="sr-only">{t("Blendet die angezeigten Hinweise aus. Aufgaben bleiben stehen, bis sie erledigt sind.", "Hides the notices shown. Tasks stay until they are done.")}</small>
        </div>
      </div>

      {zuletzt.length > 0 && (
        <div className="notice tk-undo" role="status">
          <span>{lang === "en" ? `${countLabel(zuletzt.length, "notice", "notices")} hidden. You can find them under “Done”.` : `${countLabel(zuletzt.length, "Hinweis", "Hinweise")} ausgeblendet. Du findest sie unter „Erledigt“.`}</span>
          <span className="row">
            <button type="button" disabled={busy !== null} onClick={() => void run("einblenden", () => restoreNotifications(api, zuletzt), t("Hinweise wieder eingeblendet.", "Notices shown again.")).then(() => setZuletzt([]))}>{t("Wieder einblenden", "Show again")}</button>
            <button type="button" className="tk-quiet" onClick={() => setZuletzt([])}>{t("Schließen", "Close")}</button>
          </span>
        </div>
      )}
      <Dialog open={frageAusblenden} onClose={() => setFrageAusblenden(false)} title={t("Hinweise ausblenden", "Hide notices")}>
        <p>{lang === "en"
          ? `${countLabel(infoIds.length, "notice shown", "notices shown")} will be hidden${filtered ? " (current selection only)" : ""}. Tasks stay until they are done. You can undo this right away or under “Done”.`
          : `${countLabel(infoIds.length, "angezeigter Hinweis wird", "angezeigte Hinweise werden")} ausgeblendet${filtered ? " (nur die aktuelle Auswahl)" : ""}. Aufgaben bleiben stehen, bis sie erledigt sind. Rückgängig geht direkt danach oder unter „Erledigt“.`}</p>
        <div className="row">
          <button type="button" className="primary" disabled={busy !== null} onClick={() => void ausblenden()}>
            {lang === "en" ? `Hide ${countLabel(infoIds.length, "notice", "notices")}` : countLabel(infoIds.length, "Hinweis ausblenden", "Hinweise ausblenden")}
          </button>
          <button type="button" onClick={() => setFrageAusblenden(false)}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </Dialog>
      <Dialog open={!!schliessen} onClose={() => setSchliessen(null)} title={t("Aufgabe schließen", "Close task")}>
        {schliessen && <AufgabeSchliessen v={schliessen} onDone={() => setSchliessen(null)} />}
      </Dialog>

      <Toolbar
        search={search}
        onSearch={setSearch}
        label={t("Benachrichtigungen durchsuchen", "Search notifications")}
        placeholder={t("Suchen …", "Search …")}
        filters={STATE_FILTERS.map((f) => ({ ...f, count: f.key === "offen" ? openViews.length : f.key === "ungelesen" ? unreadIds.length : undefined }))}
        filter={stateFilter}
        onFilter={setStateFilter}
      >
        <button type="button" onClick={() => { void open.refetch(); void unread.refetch(); if (stateFilter === "erledigt") void done.refetch(); }}>{t("Aktualisieren", "Refresh")}</button>
      </Toolbar>
      <div className="toolbar" role="group" aria-label={t("Art der Benachrichtigung", "Type of notification")}>
        <button type="button" aria-pressed={category === "alle"} onClick={() => setCategory("alle")}>{t("Alle Arten", "All types")}</button>
        {categories.map((c) => {
          const n = views.filter((v) => v.category === c && matchesState(v.item, stateFilter)).length;
          return (
            <button key={c} type="button" aria-pressed={category === c} onClick={() => setCategory(c)}>
              {categoryLabel(c, lang)}{n > 0 ? ` · ${n}` : ""}
            </button>
          );
        })}
      </div>

      {context.error && <p className="tk-error" role="alert">{t("Namen und Rechnungsangaben konnten nicht geladen werden. Die Benachrichtigungen stehen trotzdem hier.", "Names and invoice details could not be loaded. The notifications are still shown here.")}</p>}

      {showChat && chat && (
        <section className="panel stack" aria-labelledby="glocke-nachrichten">
          <h3 id="glocke-nachrichten">{t("Nachrichten", "Messages")}</h3>
          <Link className="task" to={chatHref}>
            <div>
              <strong>{portal === "agentur" ? t("Neue Nachrichten von Creator", "New messages from creators") : t(`Neue Nachricht von ${props.counterpart}`, `New message from ${props.counterpart}`)}</strong>
              <p>
                {lang === "en" ? countLabel(chat.count.messages, "unread message", "unread messages") : countLabel(chat.count.messages, "ungelesene Nachricht", "ungelesene Nachrichten")}
                {chat.latest.length > 1 ? t(` in ${chat.latest.length} Unterhaltungen`, ` in ${chat.latest.length} conversations`) : ""}
                {chat.latest[0]?.lastAt ? ` · ${shortWhen(chat.latest[0].lastAt, today, timeZone, lang)}` : ""}
              </p>
            </div>
            <PillView pill={pill(t(`${chat.count.messages} neu`, `${chat.count.messages} new`), "info")} />
          </Link>
        </section>
      )}

      {stateFilter !== "erledigt" && tasks.length > 0 && (
        <section className="panel stack" aria-labelledby="glocke-aufgaben">
          <h3 id="glocke-aufgaben">{t("Aufgaben", "Tasks")}</h3>
          {tasks.map((v) => row(v, whenWithDay(v.item.created_at, today, timeZone, lang)))}
        </section>
      )}
      {stateFilter !== "erledigt" && infoGroups.map((g) => (
        <section key={g.day} className="panel stack" aria-labelledby={`glocke-${g.day}`}>
          <h3 id={`glocke-${g.day}`}>{g.label}</h3>
          {g.items.map((v) => row(v, tm(v.item.created_at)))}
        </section>
      ))}
      {stateFilter !== "erledigt" && <MehrLaden rest={infoSeite.rest} groesse={infoSeite.groesse} onMehr={infoSeite.mehr} onAlle={infoSeite.alle} einheit={t("Hinweise", "notices")} />}
      {stateFilter === "erledigt" && done.isPending && <p className="muted">{t("Erledigtes wird geladen …", "Loading done items …")}</p>}
      {stateFilter === "erledigt" && done.error && <p className="tk-error" role="alert">{t("Erledigtes konnte nicht geladen werden. Versuche es erneut.", "Done items could not be loaded. Please try again.")}</p>}
      {stateFilter === "erledigt" && closedGroups.map((g) => (
        <section key={g.day} className="panel stack" aria-labelledby={`glocke-erledigt-${g.day}`}>
          <h3 id={`glocke-erledigt-${g.day}`}>{g.label}</h3>
          {g.items.map((v) => row(v, lang === "en" ? `created ${dateOf(v.item.created_at, "en", timeZone)}` : `angelegt ${date(v.item.created_at, timeZone)}`))}
        </section>
      ))}
      {stateFilter === "erledigt" && <MehrLaden rest={erledigtSeite.rest} groesse={erledigtSeite.groesse} onMehr={erledigtSeite.mehr} onAlle={erledigtSeite.alle} einheit={t("Einträge", "entries")} />}

      {nothing && !(stateFilter === "erledigt" && done.isPending) && (
        filtered && !(stateFilter === "erledigt" && !q && category === "alle") ? (
          <EmptyState title={t("Keine passenden Ergebnisse.", "No matching results.")} text={t("Passe die Suche oder die Filter an.", "Adjust the search or filters.")}
            action={<button type="button" onClick={() => { setSearch(""); setCategory("alle"); setStateFilter("offen"); }}>{t("Filter zurücksetzen", "Reset filters")}</button>} />
        ) : stateFilter === "erledigt" ? (
          <EmptyState title={t("Noch nichts erledigt.", "Nothing done yet.")} text={t("Erledigte Aufgaben und ausgeblendete Hinweise erscheinen hier.", "Completed tasks and hidden notices appear here.")} />
        ) : (
          <EmptyState title={t("Für heute ist alles erledigt.", "You're all done for today.")} text={t("Neue Aufgaben erscheinen hier automatisch.", "New tasks appear here automatically.")}
            action={<Link className="btn" to={props.calendarHref}>{t("Kalender ansehen", "View calendar")}</Link>} />
        )
      )}

      {/* R-03: Kanäle, Abwesenheit und Erinnerungen am Handy eingeklappt unter der Liste, am Rechner offen wie bisher */}
      <Klappabschnitt titel={t("Kanäle und Einstellungen", "Channels and settings")} offen={!handy}
        zusammenfassung={portal === "agentur" ? t("In-App, E-Mail, WhatsApp · Abwesenheit · Erinnerungen", "In-app, email, WhatsApp · absence · reminders") : t("In-App, E-Mail, WhatsApp · Zeitzone · Sprache", "In-app, email, WhatsApp · time zone · language")}>
        <div className="stack tk-umbau-glocke-kanaele">{props.channels}</div>
      </Klappabschnitt>
      {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p>}
    </main>
  );
}

/** Aufgabe mit Grund schließen (Befund 5): wenn der Anlass außerhalb des Portals erledigt ist. Schließt nach Erfolg (Befund 21). */
function AufgabeSchliessen({ v, onDone }: { v: NotificationView; onDone: () => void }) {
  const { busy, error, run } = useAction();
  const [grund, setGrund] = useState("");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run("schliessen", () => closeTask(api, v.item.id, grund), t("Aufgabe geschlossen. Du findest sie unter „Erledigt“.", "Task closed. You will find it under “Done”.")).then((r) => { if (r) onDone(); });
  };
  return (
    <form className="stack" onSubmit={submit}>
      <p><strong>{v.title}</strong>{v.detail ? ` · ${v.detail}` : ""}</p>
      <p>{t("Schließe die Aufgabe nur, wenn sie sich anders erledigt hat, zum Beispiel weil die Rechnung per Post kam oder die Marke abgesagt hat. Noch nicht versendete Mails dazu werden verworfen.", "Only close the task if it was resolved another way, for example because the invoice came by post or the brand cancelled. Related emails not yet sent will be discarded.")}</p>
      <label>
        {t("Grund", "Reason")}<input value={grund} onChange={(e) => setGrund(e.target.value)} maxLength={500} required minLength={3} placeholder={t("z. B. Rechnung kam per Post", "e.g. invoice came by post")} />
      </label>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy !== null || grund.trim().length < 3}>{t("Aufgabe schließen", "Close task")}</button>
        <button type="button" onClick={onDone}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </form>
  );
}

function NotificationRow({ v, when, busy, lang, onOpen, onRead, onDismiss, onAnswer, onClose, onAssign, onRelease }: {
  v: NotificationView;
  when: string;
  busy: boolean;
  lang: "de" | "en";
  onOpen: (v: NotificationView) => void;
  onRead: () => void;
  onDismiss: () => void;
  onAnswer: (response: "paid" | "still_overdue") => void;
  onClose?: () => void;
  onAssign?: () => void;
  onRelease?: () => void;
}) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  // R-03 (Umbauten 03.11.2026): eine Aktion bleibt ein Knopf, mehrere stehen hinter „Mehr“ statt als drei Knöpfe je Zeile
  const aktionen: { key: string; label: string; aria: string; onClick: () => void }[] = [
    ...(v.dismissible ? [{ key: "aus", label: t("Ausblenden", "Hide"), aria: `${t("Hinweis ausblenden", "Hide notice")}: ${v.title}`, onClick: onDismiss }] : []),
    ...(onAssign ? [{ key: "nehmen", label: t("Übernehmen", "Take over"), aria: t(`Aufgabe übernehmen: ${v.title}`, `Take over task: ${v.title}`), onClick: onAssign }] : []),
    ...(onRelease ? [{ key: "abgeben", label: t("Abgeben", "Hand back"), aria: t(`Aufgabe abgeben: ${v.title}`, `Hand back task: ${v.title}`), onClick: onRelease }] : []),
    ...(onClose ? [{ key: "zu", label: t("Schließen", "Close"), aria: t(`Aufgabe schließen: ${v.title}`, `Close task: ${v.title}`), onClick: onClose }] : []),
    ...(!v.to && v.item.state === "open" && v.item.unread ? [{ key: "gelesen", label: t("Gelesen", "Read"), aria: `${t("Als gelesen markieren", "Mark as read")}: ${v.title}`, onClick: onRead }] : []),
  ];
  return (
    <div className="task tk-row">
      <div>
        {v.to
          ? <Link className="tk-row-link" to={v.to} onClick={() => onOpen(v)}><strong>{v.title}</strong></Link>
          : <strong>{v.title}</strong>}
        <p>{[v.detail, when].filter(Boolean).join(" · ")}</p>
        {v.answerInvoice && (
          <div className="row tk-over tk-answer">
            <button type="button" className="primary" disabled={busy} onClick={() => onAnswer("paid")}>{t("Ja, das Geld ist angekommen", "Yes, the money has arrived")}</button>
            <button type="button" disabled={busy} onClick={() => onAnswer("still_overdue")}>{t("Nein, noch nicht angekommen", "No, not yet")}</button>
          </div>
        )}
      </div>
      <div className="row tk-row-end">
        {v.pills.map((p) => <PillView key={p.label} pill={p} />)}
        {aktionen.length === 1 && aktionen.map((a) => (
          <button key={a.key} type="button" className="tk-quiet tk-over" disabled={busy} aria-label={a.aria} onClick={a.onClick}>{a.label}</button>
        ))}
        {aktionen.length > 1 && (
          <span className="tk-over tk-umbau-zeilenmenue">
            <Mehr label={t(`Aktionen: ${v.title}`, `Actions: ${v.title}`)}>{(close) => (
              <>
                {aktionen.map((a) => (
                  <button key={a.key} type="button" role="menuitem" className="tk-inv-menuepunkt" disabled={busy} onClick={() => { close(); a.onClick(); }}>{a.label}</button>
                ))}
              </>
            )}</Mehr>
          </span>
        )}
      </div>
    </div>
  );
}

// ── Kanäle ──────────────────────────────────────────────────────────────────

// Ehrlich (Befunde 8, 23): „aktiv“ gibt es erst, wenn der Betrieb den Versand eingerichtet hat; bis dahin nur Probelauf
const WHATSAPP_MODE: Record<string, string> = { off: "aus", get dry_run() { return t("im Probelauf (nur protokolliert, nichts wird verschickt)", "in dry run (only logged, nothing is sent)"); }, live: "aktiv" };

function DesktopChannel({ desktop, userId, lang }: { desktop: boolean; userId: string | null; lang: "de" | "en" }) {
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const { busy, run } = useAction();
  const [permission, setPermission] = useState(desktopPermission());
  const text = permission === "unsupported" ? t("Dieser Browser zeigt keine Desktop-Hinweise.", "This browser does not show desktop notifications.")
    : permission === "denied" ? t("Im Browser blockiert. Erlaube Benachrichtigungen für CreatorOffice in den Einstellungen deines Browsers.", "Blocked in the browser. Allow notifications for CreatorOffice in your browser settings.")
      : permission === "granted" ? t("Der Browser zeigt einen Hinweis, wenn eine neue Nachricht eintrifft, solange CreatorOffice geöffnet ist.", "The browser shows a notification when a new message arrives while CreatorOffice is open.")
        : t("Der Browser muss Hinweise einmal erlauben.", "The browser has to allow notifications once.");
  return (
    <>
      <label className="row">
        <input type="checkbox" checked={desktop} disabled={!userId || busy !== null} aria-describedby="kanal-desktop"
          onChange={(e) => void run("desktop", () => saveDesktopPreference(api, userId!, e.target.checked), e.target.checked ? t("Desktop-Hinweise sind an.", "Desktop notifications are on.") : t("Desktop-Hinweise sind aus.", "Desktop notifications are off."))} />
        {t("Desktop-Hinweis bei neuen Nachrichten", "Desktop notification for new messages")}
      </label>
      <small id="kanal-desktop">{text}</small>
      {permission === "default" && desktop && (
        <div className="row">
          <button type="button" onClick={() => void Notification.requestPermission().then(() => setPermission(desktopPermission()))}>{t("Im Browser erlauben", "Allow in browser")}</button>
        </div>
      )}
    </>
  );
}

/** Kanäle der Agentur (Paket: In-App, E-Mail, WhatsApp · separate Aktivierung) mit dem, was die Datenbank wirklich tut. */
export function AgencyChannels({ orgId, isChef }: { orgId: string; isChef: boolean }) {
  return (
    <>
      <AgencyChannelsPanel orgId={orgId} isChef={isChef} />
      {/* Studio (Creator-Konto): keine Vertretung, kein Chat mit Creatorn */}
      {!istCreatorKonto() && <Abwesenheit orgId={orgId} />}
      {isChef && <Erinnerungen orgId={orgId} />}
    </>
  );
}

function AgencyChannelsPanel({ orgId, isChef }: { orgId: string; isChef: boolean }) {
  const userId = api.session()?.userId ?? null;
  const q = useChannels("agentur", orgId, null, userId);
  const { busy, run } = useAction();
  const c = q.data;
  const mode = c?.whatsappMode ?? null;
  return (
    <section className="panel stack" aria-labelledby="kanaele">
      <h3 id="kanaele">{t("Kanäle", "Channels")}</h3>
      <label className="row"><input type="checkbox" checked disabled aria-describedby="kanal-app" />In-App</label>
      <small id="kanal-app">{t("Immer an: Aufgaben und Hinweise erscheinen hier und an der Glocke.", "Always on: tasks and notices appear here and in the notifications.")}</small>
      <label className="row">
        <input type="checkbox" checked={c?.emailTasks ?? true} disabled={!c || c.emailTasks === null || busy !== null} aria-describedby="kanal-mail"
          onChange={(e) => void run("mail", () => saveEmailTasks(api, orgId, e.target.checked), e.target.checked ? t("Aufgaben kommen wieder per E-Mail.", "Tasks will be emailed again.") : t("Aufgaben kommen nur noch hier an.", "Tasks now only arrive here."))} />
        {t("E-Mail für neue Aufgaben", "Email for new tasks")}</label>
      <small id="kanal-mail">
        {ta("Neue Aufgaben (Rechnung prüfen, Zahlung gemeldet, Vertrag abgelehnt oder gegenzuzeichnen, überfällige Rechnungen, nicht zugestellte Mails) kommen zusätzlich per E-Mail, wenn sie Creator betreffen, die du siehst. Bist du als abwesend eingetragen, geht die Mail an deine Vertretung.", "New tasks (review invoice, payment reported, contract declined or awaiting countersignature, overdue invoices, undelivered emails) are also sent by email if they concern creators you can see. If you are marked as absent, the email goes to your substitute.",
          "Neue Aufgaben (Vertrag abgelehnt oder unterschrieben, überfällige Rechnungen, nicht zugestellte Mails) kommen zusätzlich per E-Mail.", "New tasks (contract declined or signed, overdue invoices, undelivered emails) are also sent by email.")}</small>
      {c?.emailTasks && (
        <label>
          {t("Aufgaben-Mails", "Task emails")}<select value={c.taskMode ?? "instant"} disabled={busy !== null}
            onChange={(e) => void run("modus", () => saveMemberMailPrefs(api, orgId, { tasksMode: e.target.value as "instant" | "daily" }),
              e.target.value === "daily" ? t("Aufgaben kommen einmal am Tag gebündelt.", "Tasks arrive bundled once a day.") : t("Aufgaben kommen sofort.", "Tasks arrive immediately."))}>
            <option value="instant">{t("Sofort, eine Mail je Aufgabe", "Immediately, one email per task")}</option>
            <option value="daily">{t("Einmal am Tag als Übersicht (morgens)", "Once a day as a summary (mornings)")}</option>
          </select>
        </label>
      )}
      {!istCreatorKonto() && <>
      <label className="row">
        <input type="checkbox" checked={c?.emailChat ?? true} disabled={!c || c.emailChat === null || busy !== null} aria-describedby="kanal-chat"
          onChange={(e) => void run("chat", () => saveEmailChat(api, orgId, e.target.checked), e.target.checked ? t("Ungelesene Nachrichten kommen wieder per E-Mail.", "Unread messages will be emailed again.") : t("Nachrichten siehst du nur noch hier.", "You will now only see messages here."))} />
        {t("E-Mail bei ungelesenen Nachrichten", "Email for unread messages")}</label>
      <small id="kanal-chat">
        {t("Schreibt ein Creator und niemand liest es eine Weile, kommt eine Sammelmail an die zuständigen Personen: wer den Creator betreut oder zuletzt im Gespräch geschrieben hat (bei Abwesenheit die Vertretung), sonst an die Admins.", "If a creator writes and nobody reads it for a while, a digest email goes to the people responsible: whoever manages the creator or last wrote in the conversation (their substitute if absent), otherwise the admins.")}</small>
      <label className="row"><input type="checkbox" checked={false} disabled aria-describedby="kanal-whatsapp" />{t("WhatsApp · in Vorbereitung", "WhatsApp · in preparation")}</label>
      <small id="kanal-whatsapp">
        {t("WhatsApp-Hinweise an Creator sind ", "WhatsApp notices to creators are ")}{mode ? WHATSAPP_MODE[mode] ?? mode : t("nicht lesbar", "not readable")}{t(". Der Versand über WhatsApp ist noch nicht eingerichtet; bis dahin verschickt CreatorOffice nichts über WhatsApp, der Probelauf protokolliert nur, was rausgehen würde. Hinweise gehen später nur an Creator mit Handynummer und Einwilligung.", ". Sending via WhatsApp is not set up yet; until then CreatorOffice sends nothing via WhatsApp, the dry run only logs what would go out. Notices will later only go to creators with a mobile number and consent.")}{isChef ? <> <Link to="/agentur/einstellungen/abo">{t("Einstellungen · Abo", "Settings · Subscription")}</Link></> : t(" Ändern kann nur der Admin.", " Only the admin can change this.")}
      </small>
      {c && <PersoenlicherAbsender key={`${c.replyTo ?? ""}|${c.signature ?? ""}`} orgId={orgId} c={c} />}
      </>}
      {isChef && <p><Link to="/agentur/postausgang">{t("Postausgang öffnen", "Open outbox")}</Link>{ta(": alle Mails der Agentur mit Status, Fehlern und „Erneut senden“.", ": all agency emails with status, errors and “Send again”.", ": alle deine Mails mit Status, Fehlern und „Erneut senden“.", ": all your emails with status, errors and “Send again”.")}</p>}
      {c && <DesktopChannel desktop={c.desktop} userId={userId} lang="de" />}
    </section>
  );
}

/** Eigene Antwortadresse und Signatur für Mails, die du auslöst (gilt, wenn die Agentur den persönlichen Absender nutzt; Befund 17). */
function PersoenlicherAbsender({ orgId, c }: { orgId: string; c: Channels }) {
  const { busy, error, run } = useAction();
  const [reply, setReply] = useState(c.replyTo ?? "");
  const [sig, setSig] = useState(c.signature ?? "");
  return (
    <details>
      <summary>{t("Deine Antwortadresse und Signatur", "Your reply address and signature")}</summary>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); void run("absender", () => saveMemberMailPrefs(api, orgId, { replyTo: reply, signature: sig, clear: !reply.trim() && !sig.trim() }), t("Gespeichert.", "Saved.")); }}>
        <p className="muted">{t("Gilt für Mails, die du auslöst (Termin, Rechnung anfordern, Korrektur …), wenn der Admin unter Einstellungen · Vorlagen den persönlichen Absender eingeschaltet hat. Sonst gilt die Adresse der Agentur.", "Applies to emails you trigger (appointment, request invoice, correction …) if the admin has switched on the personal sender under Settings · Templates. Otherwise the agency address applies.")}</p>
        <label>{t("Antwortadresse", "Reply address")}<input type="email" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="vorname@agentur.example" maxLength={200} /></label>
        <label>{t("Signatur", "Signature")}<textarea rows={3} value={sig} onChange={(e) => setSig(e.target.value)} maxLength={1000} placeholder={t("Vorname Nachname\nTalent-Management · Telefon", "First name Last name\nTalent management · Phone")} /></label>
        {error && <p className="tk-error" role="alert">{error}</p>}
        <div className="row"><button type="submit" className="primary" disabled={busy !== null}>{t("Speichern", "Save")}</button></div>
      </form>
    </details>
  );
}

/** Abwesenheit mit Vertretung (Praxisfall): Sammelmails und Aufgaben-Mails gehen in der Zeit an die Vertretung. */
function Abwesenheit({ orgId }: { orgId: string }) {
  const userId = api.session()?.userId ?? null;
  const q = useChannels("agentur", orgId, null, userId);
  const members = useMembers(orgId);
  if (!q.data) return null;
  return <AbwesenheitForm key={`${q.data.awayFrom}|${q.data.awayUntil}|${q.data.awaySubstitute}`} orgId={orgId} c={q.data}
    team={(members.data ?? []).filter((m) => m.user_id !== userId)} />;
}
function AbwesenheitForm({ orgId, c, team }: { orgId: string; c: Channels; team: { user_id: string; display_name: string | null }[] }) {
  const { busy, error, run } = useAction();
  const [von, setVon] = useState(c.awayFrom ?? "");
  const [bis, setBis] = useState(c.awayUntil ?? "");
  const [vertretung, setVertretung] = useState(c.awaySubstitute ?? "");
  const [notiz, setNotiz] = useState(c.awayNote ?? "");
  const aktiv = !!c.awayFrom;
  return (
    <form className="panel stack" aria-labelledby="abwesenheit" onSubmit={(e) => {
      e.preventDefault();
      void run("abwesenheit", () => saveAbsence(api, orgId, { from: von || null, until: bis || null, substitute: vertretung || null, note: notiz || null }),
        von ? t("Abwesenheit gespeichert. Creator sehen im Chat, wer dich vertritt.", "Absence saved. Creators see in the chat who is standing in for you.") : t("Abwesenheit entfernt.", "Absence removed."));
    }}>
      <h3 id="abwesenheit">{t("Abwesenheit", "Absence")}</h3>
      <p>{t("Urlaub oder krank: In dieser Zeit gehen Sammelmails zu ungelesenen Nachrichten und Aufgaben-Mails an deine Vertretung. Deine Creator sehen im Chat, bis wann du weg bist und wer antwortet.", "Holiday or sick: during this time, digest emails about unread messages and task emails go to your substitute. Your creators see in the chat until when you are away and who replies.")}</p>
      <div className="grid">
        <label>{t("Von", "From")}<input type="date" value={von} onChange={(e) => setVon(e.target.value)} /></label>
        <label>{t("Bis", "Until")}<input type="date" value={bis} min={von || undefined} onChange={(e) => setBis(e.target.value)} /></label>
        <label>{t("Vertretung", "Substitute")}<select value={vertretung} onChange={(e) => setVertretung(e.target.value)}>
            <option value="">{t("Keine (Admin übernimmt)", "None (admin takes over)")}</option>
            {team.map((m) => <option key={m.user_id} value={m.user_id}>{m.display_name ?? "Team"}</option>)}
          </select>
        </label>
        <label>{t("Notiz (optional)", "Note (optional)")}<input value={notiz} maxLength={200} onChange={(e) => setNotiz(e.target.value)} placeholder={t("z. B. Urlaub", "e.g. holiday")} /></label>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy !== null || (!!von !== !!bis)}>{t("Abwesenheit speichern", "Save absence")}</button>
        {aktiv && <button type="button" disabled={busy !== null} onClick={() => void run("abwesenheit-weg", () => saveAbsence(api, orgId, { from: null, until: null, substitute: null, note: null }), t("Abwesenheit entfernt.", "Absence removed."))}>{t("Abwesenheit entfernen", "Remove absence")}</button>}
      </div>
    </form>
  );
}

/** Erinnerungen vor Fristen und Chat-Sammelmail (Befunde 7, 14, 15): Vorlauf je Agentur, 0 schaltet ab. Nur Admins. */
function Erinnerungen({ orgId }: { orgId: string }) {
  const q = useQuery({ queryKey: ["org_reminder_settings", orgId], queryFn: () => loadReminders(api, orgId) });
  if (!q.data) return null;
  return <ErinnerungenForm key={JSON.stringify(q.data)} orgId={orgId} start={q.data} />;
}
function ErinnerungenForm({ orgId, start }: { orgId: string; start: ReminderSettings }) {
  const { busy, error, run } = useAction();
  const [r, setR] = useState<ReminderSettings>(start);
  const feld = (k: keyof ReminderSettings, label: string, max: number, einheit: string, hinweis: string) => (
    <label>
      {label}
      <input type="number" min={0} max={max} value={r[k]} onChange={(e) => setR((x) => ({ ...x, [k]: Math.max(0, Math.min(max, Number(e.target.value) || 0)) }))} />
      <small>{einheit}. {hinweis}</small>
    </label>
  );
  return (
    <form className="panel stack" aria-labelledby="erinnerungen" onSubmit={(e) => { e.preventDefault(); void run("erinnerungen", () => saveReminders(api, orgId, r), t("Erinnerungen gespeichert.", "Reminders saved.")); }}>
      <h3 id="erinnerungen">{t("Erinnerungen", "Reminders")}</h3>
      <p>{t("Der tägliche Prüflauf erinnert vor Fristen. 0 schaltet eine Erinnerung ab.", "The daily check sends reminders before deadlines. 0 switches a reminder off.")}</p>
      <div className="tk-reminders">
        {feld("content_deadline_days", t("Content-Deadline", "Content deadline"), 30, t("Tage vorher", "days before"), ta("Hinweis an den Creator, per Mail, wenn er Hinweise nicht abbestellt hat. Gilt auch für die Entwurfsfristen einzelner Beiträge (Erinnerung am Vortag); 0 schaltet beides ab.", "Notice to the creator by email, unless they have unsubscribed from notices. Also applies to draft deadlines of individual posts (reminder the day before); 0 switches both off.",
          "Erinnerung an dich per Mail. Gilt auch für die Entwurfsfristen einzelner Beiträge (Erinnerung am Vortag); 0 schaltet beides ab.", "Reminder to you by email. Also applies to draft deadlines of individual posts (reminder the day before); 0 switches both off."))}
        {feld("usage_rights_days", t("Ende der Nutzungsrechte", "End of usage rights"), 180, t("Tage vorher", "days before"), ta("Aufgabe fürs Team, Verlängerung anbieten.", "Task for the team: offer an extension.", "Aufgabe für dich, Verlängerung anbieten.", "Task for you: offer an extension."))}
        {/* Studio (Creator-Konto): keine Creator-Rechnungen an eine Agentur, kein Chat */}
        {!istCreatorKonto() && feld("invoice_missing_days", t("Creator-Rechnung fehlt", "Creator invoice missing"), 60, t("Tage nach der Anforderung", "days after the request"), t("Aufgabe fürs Team.", "Task for the team."))}
        {!istCreatorKonto() && feld("chat_digest_minutes", t("Chat-Sammelmail", "Chat digest email"), 1440, t("Minuten ungelesen", "minutes unread"), t("Dann geht eine Mail an die andere Seite.", "Then an email goes to the other side."))}
        {/* 0094 (AUTOMATIK-R3-5): Uhrzeit der täglichen Übersicht, frühestens nach den Tagesläufen */}
        <label>
          {t("Tägliche Übersicht", "Daily summary")}
          <select value={r.digest_hour} aria-describedby="erinnerungen-uebersicht" onChange={(e) => setR((x) => ({ ...x, digest_hour: Number(e.target.value) }))}>
            {DIGEST_HOURS.map((h) => <option key={h} value={h}>{t(`${h} Uhr`, `${h} a.m.`)}</option>)}
          </select>
          <small id="erinnerungen-uebersicht">{t("Für alle, die Mails einmal am Tag gebündelt wollen, in ihrer eigenen Zeitzone. Sie kommt erst, wenn der tägliche Prüflauf durch ist, damit dessen Aufgaben und Erinnerungen am selben Morgen dabei sind.",
            "For everyone who wants emails bundled once a day, in their own time zone. It is sent only after the daily check has finished, so its tasks and reminders are included the same morning.")}</small>
        </label>
      </div>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <div className="row">
        <button type="submit" className="primary" disabled={busy !== null}>{t("Erinnerungen speichern", "Save reminders")}</button>
        <button type="button" disabled={busy !== null} onClick={() => setR(DEFAULT_REMINDERS)}>{t("Standard", "Default")}</button>
      </div>
    </form>
  );
}

const VIA: Record<string, [string, string]> = {
  creator: ["von dir", "by you"], team: ["von deiner Agentur", "by your agency"], link: ["über den Abmeldelink", "via the unsubscribe link"],
  complaint: ["nach einer Spam-Meldung", "after a spam report"],
};

/** Kanäle im Creator-Portal in Du-Form: E-Mail an die hinterlegte Adresse, WhatsApp nur mit deiner Einwilligung. */
export function CreatorChannels({ orgId, talentId, timeZone }: { orgId: string; talentId: string; timeZone: string }) {
  const lang = useLang();
  const t = (de: string, en: string) => (lang === "en" ? en : de);
  const userId = api.session()?.userId ?? null;
  const q = useChannels("creator", orgId, talentId, userId);
  const { busy, run } = useAction();
  const c = q.data;
  const whatsapp = !!(c?.whatsappOptIn && c.whatsappPhone);
  const zonen = zonenAuswahl(c?.timeZone, timeZone);
  const fmt = (d: string) => (lang === "en" ? dateOf(d, "en", timeZone) : date(d, timeZone));
  return (
    <section className="panel stack" aria-labelledby="kanaele">
      <h3 id="kanaele">{t("Kanäle", "Channels")}</h3>
      <label className="row"><input type="checkbox" checked disabled aria-describedby="kanal-app" />In-App</label>
      <small id="kanal-app">{t("Immer an: Aufgaben und Hinweise deiner Agentur erscheinen hier und an der Glocke.", "Always on: tasks and notices from your agency appear here and at the bell.")}</small>
      <label className="row"><input type="checkbox" checked={!!c?.email} disabled aria-describedby="kanal-mail" />{t("E-Mail für Aufgaben", "Email for tasks")}</label>
      <small id="kanal-mail">
        {c?.email
          ? t(`Aufgaben kommen immer auch per E-Mail an ${c.email}: Rechnungsanforderungen, Korrekturen, Zahlungsnachfragen, geänderte Deadlines und Verträge zur Unterschrift (mit dem Link zum Unterschreiben).`,
            `Tasks always also arrive by email at ${c.email}: invoice requests, corrections, payment checks, changed deadlines and contracts to sign (with the signing link).`)
          : t("Deine Agentur hat keine eigene E-Mail-Adresse von dir hinterlegt; Mails gehen an die Adresse, mit der du dich anmeldest.",
            "Your agency has not stored a separate email address for you; emails go to the address you sign in with.")}
      </small>
      <label className="row">
        <input type="checkbox" checked={!(c?.emailInfoOff ?? false)} disabled={!c || busy !== null} aria-describedby="kanal-info"
          onChange={(e) => void run("info", () => saveEmailInfoOff(api, orgId, talentId, !e.target.checked), e.target.checked ? t("Du bekommst Hinweise wieder per E-Mail.", "You will get notices by email again.") : t("Hinweise siehst du nur noch hier.", "You will only see notices here."))} />
        {t("E-Mail für Hinweise", "Email for notices")}
      </label>
      {/* vollständige Liste, was trotzdem per Mail kommt (AUTOMATIK-R3-10); Abgleich mit der Datenbank im Test */}
      <small id="kanal-info">{creatorMailHints(lang).info}</small>
      <label className="row">
        <input type="checkbox" checked={!(c?.chatMailOff ?? false)} disabled={!c || busy !== null} aria-describedby="kanal-chat"
          onChange={(e) => void run("chat", () => saveTalentPreferences(api, talentId, { chatMail: e.target.checked }), e.target.checked ? t("Ungelesene Nachrichten kommen wieder per E-Mail.", "Unread messages will arrive by email again.") : t("Nachrichten siehst du nur noch hier.", "You will only see messages here."))} />
        {t("E-Mail bei ungelesenen Nachrichten", "Email for unread messages")}
      </label>
      <small id="kanal-chat">{t("Schreibt dir deine Agentur und du liest es eine Weile nicht, kommt eine kurze Mail mit der Nachricht.", "If your agency writes to you and you don't read it for a while, you get a short email with the message.")}</small>
      <label>
        {t("Mails bündeln", "Bundle emails")}
        <select value={c?.taskMode ?? "instant"} disabled={!c || busy !== null} aria-describedby="kanal-buendeln"
          onChange={(e) => void run("modus", () => saveTalentPreferences(api, talentId, { taskMode: e.target.value as "instant" | "daily" }), t("Gespeichert.", "Saved."))}>
          {/* kurz genug für 375 px (SICHT-CREATOR-20); was dazugehört, sagt der Satz darunter */}
          <option value="instant">{t("Sofort, je Meldung eine Mail", "Right away, one email each")}</option>
          <option value="daily">{t("Einmal am Tag als Übersicht (morgens)", "Once a day as a summary (mornings)")}</option>
        </select>
        <small id="kanal-buendeln">{creatorMailHints(lang).immediate}</small>
      </label>
      <label>
        {t("Sprache der E-Mails dieser Agentur", "Email language for this agency")}
        <select value={c?.language ?? ""} disabled={!c || busy !== null} aria-describedby="kanal-sprache"
          onChange={(e) => void run("sprache", () => saveTalentPreferences(api, talentId, { language: (e.target.value || null) as "de" | "en" | null }), t("Sprache gespeichert.", "Language saved."))}>
          {/* ohne eigene Wahl folgen die Mails der Sprache des Portals (app.talent_language), nicht der Agentur (SICHT-CREATOR-17) */}
          <option value="">{t(`Wie dein Portal (${mailLanguageName(lang, "de")})`, `Same as your portal (${mailLanguageName(lang, "en")})`)}</option>
          <option value="de">{mailLanguageName("de", lang)}</option>
          <option value="en">{mailLanguageName("en", lang)}</option>
        </select>
        <small id="kanal-sprache">{t("Gilt nur für Mails dieser Agentur. Die Portalsprache stellst du im Profil ein; wechselst du sie dort, folgt auch diese Einstellung.",
          "Applies only to emails from this agency. You set the portal language in your profile; changing it there also changes this setting.")}</small>
      </label>
      {userId && (
        <label>
          {t("Deine Zeitzone", "Your time zone")}
          <select value={c?.timeZone ?? ""} disabled={!c || busy !== null} aria-describedby="kanal-zone"
            onChange={(e) => void run("zone", () => saveTimeZone(api, userId, e.target.value || null), t("Zeitzone gespeichert.", "Time zone saved."))}>
            <option value="">{t("Automatisch (Browser)", "Automatic (browser)")}</option>
            <optgroup label={t("Häufig", "Common")}>{zonen.haeufig.map((z) => <option key={z} value={z}>{zonenName(z)}</option>)}</optgroup>
            <optgroup label={t("Alle Zonen", "All time zones")}>{zonen.alle.map((z) => <option key={z} value={z}>{zonenName(z)}</option>)}</optgroup>
          </select>
          <small id="kanal-zone">{t("Uhrzeiten in Kalender, Glocke und Termin-Mails stehen in dieser Zone.", "Times in the calendar, notifications and date emails are shown in this time zone.")}</small>
        </label>
      )}
      {c?.prefsChangedVia && c.prefsChangedAt && (
        <small>{t("Zuletzt geändert", "Last changed")} {VIA[c.prefsChangedVia]?.[lang === "en" ? 1 : 0] ?? ""} {t("am", "on")} {fmt(c.prefsChangedAt)}.</small>
      )}
      <label className="row"><input type="checkbox" checked={whatsapp} disabled aria-describedby="kanal-whatsapp" />{t("WhatsApp · separate Aktivierung", "WhatsApp · separate activation")}</label>
      <small id="kanal-whatsapp">
        {whatsapp && c?.whatsappOptIn ? t(`Du hast am ${fmt(c.whatsappOptIn)} eingewilligt. `, `You consented on ${fmt(c.whatsappOptIn)}. `) : t("Du hast noch nicht eingewilligt. ", "You have not consented yet. ")}
        {t("WhatsApp-Hinweise sind noch in Vorbereitung; bis dahin kommt nichts über WhatsApp.", "WhatsApp notifications are still being set up; until then nothing is sent via WhatsApp.")}{" "}
        <Link to="/creator/profil">{t("Einwilligung im Profil ändern", "Change consent in your profile")}</Link>
      </small>
      {c && <DesktopChannel desktop={c.desktop} userId={userId} lang={lang} />}
    </section>
  );
}
